"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormulaValidator = void 0;
const sheet_db_1 = require("../../../../sheet_db");
const utils_1 = require("../../../../utils");
const antlr_files_1 = require("../../../antlr_files");
const boolean_node_1 = require("../../boolean.node");
const column_node_1 = require("../../column.node");
const comparison_node_1 = require("../../comparison.node");
const constant_node_1 = require("../../constant.node");
const foreign_node_1 = require("../../foreign.node");
const formula_node_1 = require("../../formula.node");
const math_nodes_1 = require("../../math_nodes");
class FormulaValidator {
    node;
    _semanticErrors;
    constructor(node) {
        this._semanticErrors = [];
        this.node = node;
    }
    get semanticErrors() {
        return this._semanticErrors;
    }
    validateExistentFormula = () => {
        if (!this.node.formulaDef)
            this._semanticErrors.push({
                message: `Formula <${this.node.formulaNameToken}> not found`,
                from: this.node.startPosition ?? 0,
                to: this.node.endPosition ?? 1,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.FUNCTION_NOT_FOUND,
                token: this.node.formulaNameToken,
                error_type: 'semantic',
            });
    };
    validateRequiredArgsCount = () => {
        const { formulaDef, children } = this.node;
        if (!formulaDef)
            return;
        const { args_def } = formulaDef;
        let requiredArgsCount = 0;
        args_def.forEach(argDef => {
            if (argDef.required)
                requiredArgsCount++;
        });
        if (children.length < requiredArgsCount)
            this._semanticErrors.push({
                message: `expected ${requiredArgsCount} required arguments, received ${this.node.children.length} on <${this.node.formulaName}>`,
                from: this.node.argsStartPosition ?? 0,
                to: this.node.argsEndPosition ?? 1,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.MISSING_ARGUMENTS,
                token: this.node.formulaNameToken,
                error_type: 'semantic',
            });
        const lastArgDef = args_def[args_def.length - 1];
        // more arguments than args on def, and last arg is not variadic
        if (children.length > args_def.length && !lastArgDef.variadic) {
            const firstInvalidArg = children[args_def.length];
            this._semanticErrors.push({
                message: `received ${children.length}, expected ${args_def.length} arguments`,
                from: firstInvalidArg.startPosition,
                to: this.node.argsEndPosition ?? firstInvalidArg.endPosition,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.UNEXPECTED_ARGUMENT,
                token: this.node.formulaNameToken,
                error_type: 'semantic',
            });
        }
    };
    validateWindowFunction() {
        const wfValidationMap = {
            aggregation: () => {
                this.validateWfIsRoot();
                this.validateRangeArgs();
            },
            rolling_sum: () => {
                this.validateWfIsRoot();
                this.validateWfArgs();
            },
        };
        const summarization = this.node.formulaDef?.special_fn_type;
        if (summarization)
            wfValidationMap[summarization]();
    }
    validateRangeArgs() {
        const allowedRangeComparisonTypes = [
            sheet_db_1.FormulaComparisonType.RANGE_AND_COLUMN,
            sheet_db_1.FormulaComparisonType.RANGE_AND_CONSTANT,
        ];
        const argDefs = this.node.formulaDef?.args_def;
        if (!argDefs)
            return;
        this.node.children.forEach((arg, i) => {
            const argDef = argDefs[Math.min(i, argDefs.length - 1)];
            if (argDef.types.length !== 1)
                return;
            if (argDef.types[0] !== 'boolean') {
                this.validateStrictType(arg, argDef.types[0]);
                return;
            }
            if (!(arg instanceof comparison_node_1.ComparisonExprNode))
                return this._semanticErrors.push({
                    message: `Boolean argument expected to be a comparison`,
                    from: this.node.startPosition ?? 0,
                    to: this.node.endPosition ?? 1,
                    severity: 'error',
                    error_code: sheet_db_1.SemanticErrorCodes.INVALID_BOOLEAN_COMPARISON,
                    token: arg.toString(),
                    error_type: 'semantic',
                });
            const comparison = arg;
            const type = comparison.getComparisonType();
            if (!allowedRangeComparisonTypes.includes(type))
                this._semanticErrors.push({
                    message: `Invalid type of boolean comparison, expecting range`,
                    from: this.node.startPosition ?? 0,
                    to: this.node.endPosition ?? 1,
                    severity: 'error',
                    error_code: sheet_db_1.SemanticErrorCodes.INVALID_BOOLEAN_COMPARISON,
                    token: arg.toString(),
                    error_type: 'semantic',
                });
        });
    }
    validateWfIsRoot() {
        if ((0, sheet_db_1.isSpecialFunctionCheck)(this.node.formulaDef) && this.node.parent)
            this._semanticErrors.push({
                message: `window functions cannot be used as arguments`,
                from: this.node.startPosition ?? 0,
                to: this.node.endPosition ?? 1,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.WINDOW_FUNCTION_NOT_ROOT,
                token: this.node.toString(),
                error_type: 'semantic',
            });
    }
    validateWfArgs() {
        const args = this.node.children;
        args.forEach((possibleArg, argIndex) => {
            let arg = possibleArg;
            // skip parenthesis nodes and get actual argument
            while (arg instanceof math_nodes_1.ParenthesisExprNode) {
                // parenthesis node filter childrens on get method to return the expression
                arg = arg.children[0];
            }
            const allowedTypes = this.validateVariadicAndGetTypes(argIndex);
            if (!allowedTypes.length)
                return;
            if (arg instanceof constant_node_1.ConstantNode) {
                this.validateConstantArg(arg, allowedTypes, argIndex);
                return;
            }
            // allows a column node or a foreign node as an argument (the foreign node is only allowed on aggregation functions for now)
            if (arg instanceof column_node_1.ColumnNode || arg instanceof foreign_node_1.ForeignNode)
                return;
            this._semanticErrors.push({
                message: `window functions cannot have operations or functions as arguments`,
                from: arg.startPosition,
                to: arg.endPosition,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.WINDOW_FUNCTION_INVALID_DATA_TYPE,
                token: arg.toString(),
                error_type: 'semantic',
            });
        });
    }
    validateArgs = () => {
        const args = this.node.children;
        args.forEach((possibleArg, argIndex) => {
            let arg = possibleArg;
            // skip parenthesis nodes and get actual argument
            while (arg instanceof math_nodes_1.ParenthesisExprNode) {
                // parenthesis node filter childrens on get method to return the expression
                arg = arg.children[0];
            }
            const allowedTypes = this.validateVariadicAndGetTypes(argIndex);
            if (!allowedTypes.length)
                return;
            if (allowedTypes.length === 1)
                this.validateStrictType(arg, allowedTypes[0]);
            if (arg instanceof constant_node_1.ConstantNode)
                this.validateConstantArg(arg, allowedTypes, argIndex);
            if (arg instanceof formula_node_1.FormulaNode)
                this.validateFormulaArg(arg, allowedTypes);
            if (arg instanceof boolean_node_1.BooleanExprNode && !allowedTypes.includes('boolean'))
                this.validateBoolExprArg(allowedTypes, arg, '<boolean expression>');
            if (arg instanceof comparison_node_1.ComparisonExprNode && !allowedTypes.includes('boolean'))
                this.validateBoolExprArg(allowedTypes, arg, '<comparison expression>');
            if (arg instanceof math_nodes_1.PlusMinusExprNode ||
                arg instanceof math_nodes_1.PowExprNode ||
                arg instanceof math_nodes_1.SignedExprNode ||
                arg instanceof math_nodes_1.TimesDivExprNode) {
                // validate math arg
            }
        });
    };
    validateStrictType(arg, expectedType) {
        // Validations for strict typed formulas
        const strictValidations = new Map([
            [
                'range_reference',
                arg => {
                    if (!(arg instanceof foreign_node_1.ForeignNode))
                        this._semanticErrors.push({
                            message: `Argument expected to be a range reference`,
                            from: arg.startPosition,
                            to: arg.endPosition,
                            severity: 'error',
                            error_code: sheet_db_1.SemanticErrorCodes.UNEXPECTED_ARGUMENT,
                            token: arg.text,
                            error_type: 'semantic',
                        });
                },
            ],
            [
                'column',
                arg => {
                    if (!(arg instanceof column_node_1.ColumnNode))
                        this._semanticErrors.push({
                            message: `Argument expected to be a column`,
                            from: arg.startPosition,
                            to: arg.endPosition,
                            severity: 'error',
                            error_code: sheet_db_1.SemanticErrorCodes.UNEXPECTED_ARGUMENT,
                            token: arg.text,
                            error_type: 'semantic',
                        });
                },
            ],
        ]);
        const validator = strictValidations.get(expectedType);
        validator?.(arg);
    }
    validateFormulaArg = (arg, allowedTypes) => {
        // expect formula data to be filled (on translation process)
        if (!arg.formulaData)
            return;
        const columnTypeToArgConverter = {
            [sheet_db_1.ColumnType.BOOLEAN]: 'boolean',
            [sheet_db_1.ColumnType.STRING]: 'string',
            [sheet_db_1.ColumnType.NUMBER]: 'number',
            [sheet_db_1.ColumnType.CURRENCY]: 'number',
            [sheet_db_1.ColumnType.ERROR]: 'string',
            [sheet_db_1.ColumnType.DATE]: 'date',
            [sheet_db_1.ColumnType.SERIAL_DATE]: 'number',
            [sheet_db_1.ColumnType.EMPTY]: 'string',
            [sheet_db_1.ColumnType.INTEGER]: 'int',
            [sheet_db_1.ColumnType.JSON]: 'string',
        };
        const argReturnType = arg.formulaData.returnType;
        const castedArgReturnType = columnTypeToArgConverter[argReturnType];
        if (!allowedTypes.includes(castedArgReturnType))
            this._semanticErrors.push({
                message: `Formula return type not compatible. Expected one of [${allowedTypes}]`,
                from: arg.startPosition,
                to: arg.endPosition,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.INVALID_DATA_TYPE,
                token: arg.formulaNameToken,
                error_type: 'semantic',
            });
    };
    validateConstantArg = (arg, allowedTypes, argIndex) => {
        const receivedArgType = this.getArgType(arg, allowedTypes, argIndex);
        if (!receivedArgType)
            this._semanticErrors.push({
                message: `expected one of [${allowedTypes}]`,
                from: arg.startPosition,
                to: arg.endPosition,
                severity: 'error',
                error_code: sheet_db_1.SemanticErrorCodes.INVALID_DATA_TYPE,
                token: arg.token,
                error_type: 'semantic',
            });
    };
    getArgType = (arg, expectedArgTypes, argIndex) => {
        for (let i = 0; i < expectedArgTypes.length; i++) {
            const expectedArgType = expectedArgTypes[i];
            if (expectedArgType === 'boolean' && arg.terminalType === antlr_files_1.Parser.BOOL)
                return expectedArgType;
            if (expectedArgType === 'number' && antlr_files_1.Parser.NUMBER_TYPES.has(arg.terminalType))
                return expectedArgType;
            if (expectedArgType === 'date') {
                const parsed = (0, utils_1.doParse)(String(arg.abstractText), ['yyyy-MM-dd']);
                if (parsed)
                    return expectedArgType;
            }
            if (expectedArgType.startsWith('enum')) {
                const enums = this.getEnums(expectedArgType);
                const translatedEnums = this.translateEnums(expectedArgType, argIndex);
                if (!enums.includes(String(arg.abstractText).trim())) {
                    this._semanticErrors.push({
                        message: `expected one of ${translatedEnums}`,
                        from: arg.startPosition,
                        to: arg.endPosition,
                        severity: 'error',
                        error_code: sheet_db_1.SemanticErrorCodes.UNEXPECTED_ENUM,
                        token: arg.token,
                        error_type: 'semantic',
                    });
                }
                return expectedArgType;
            }
            if (expectedArgType === 'string' && arg.terminalType === antlr_files_1.Parser.STRING)
                return expectedArgType;
        }
        return null;
    };
    getEnums = (unparsedEnum) => {
        return (0, utils_1.replaceAll)((0, utils_1.replaceAll)((0, utils_1.replaceAll)(unparsedEnum, 'enum', ''), '(', ''), ')', '')
            .split('|')
            .map(s => s.trim().toUpperCase());
    };
    translateEnums = (enums, argIndex) => {
        const translations = this.node.formulaTranslations;
        const enumReplacement = translations?.replacements?.[argIndex];
        if (enumReplacement)
            Object.entries(enumReplacement).forEach(([localeEnum, canonicalEnum]) => {
                enums = enums.replace(canonicalEnum, localeEnum);
            });
        return enums;
    };
    validateVariadicAndGetTypes = (argIndex) => {
        if (!this.node.formulaDef)
            return [];
        const { args_def } = this.node.formulaDef;
        const is_window_function = (0, sheet_db_1.isSpecialFunctionCheck)(this.node.formulaDef);
        const argDef = args_def[argIndex];
        const lastArgDef = args_def[args_def.length - 1];
        //unexpected arg and not variadic
        if (!argDef && !lastArgDef.variadic) {
            return [];
        }
        const types = argDef?.types ?? lastArgDef.types;
        const canBeNumber = types.includes('number') || types.includes('int');
        // force accepting string types on numeric args
        if (!is_window_function && canBeNumber && !types.includes('string'))
            types.push('string');
        return types;
    };
    validateBoolExprArg(allowedTypes, arg, type) {
        this._semanticErrors.push({
            message: `expected one of [${allowedTypes}]. received ${type} "${arg.toString()}"`,
            from: arg.startPosition,
            to: arg.endPosition,
            severity: 'error',
            error_code: sheet_db_1.SemanticErrorCodes.INVALID_DATA_TYPE,
            token: arg.toString(),
            error_type: 'semantic',
        });
    }
}
exports.FormulaValidator = FormulaValidator;
